import { BANK_FILE_STEP_LABELS } from "@hakili/domain-types";
import { t } from "@hakili/i18n";
import { BANK_FS_META, Track, type TrackSegment } from "@hakili/ui";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

import type { AnalysisGradeView } from "./analyse.js";
import { DASH, type FichierBanque, fill, headSubLineParts, initialsOf, StatusChip } from "./dossiers.js";
import { GradeMedal } from "./grade-medal.js";
import { perduChipWords } from "./validation.js";

/**
 * WF-BNK-04, the workspace, to the drawing (`hakili-bank.jsx`,
 * `WorkspaceScreen`, `StageStepper`, `StagePanel`) since round `L-0`: the
 * breadcrumb, « <client> · Crédit auto » with the stage chip and the solidity
 * chip (a dash until intelligence's note), the reception line derived from
 * the submission and the pieces the bank's own door lists, « Interroger Hakili »
 * greyed with its round, the stepper with its EIGHT stages , the delegation that
 * hides « Comité » reads a policy no door writes before `L-1`'s `M2`, so every
 * stage is drawn , and the stage panel: the reception banner with « Prendre en
 * revue » VISIBLE (the palette's own shades), the review flow, or the later stages
 * in words with their round.
 *
 * `A-1a` (D-1021-1, D3 §2): the stepper is the kit's `Track` (the parcours), no longer a card of its own;
 * the grade medal is never amber (R-13); « Historique », which had no door, is no longer drawn (D-1019-2).
 */
const STAGES = [
  "nouveau",
  "revue",
  "validation",
  "offre",
  "comite",
  "signature",
  "conditions",
  "decaisse",
] as const;
const STEP_LABELS = new Map<string, string>(Object.entries(BANK_FILE_STEP_LABELS));

/**
 * D2 `ui.js:58` and `core.js:25`: the head's chip is the stage in the parcours' own words (« Revue », « Validation », « Offre », « Comité »,
 * « Conditions »), « Chez Hakili » at `retour`, « Refusé » at `refuse`; a lost file keeps the row's own reason (`perduChipWords`).
 */
const chipWords = (
  stage: string,
  lost: { readonly reason: string; readonly at: string; readonly detail?: string | null } | null,
): string | undefined =>
  stage === "perdu"
    ? perduChipWords(lost)
    : stage === "retour"
      ? t("bank.dossier.chezHakili")
      : STEP_LABELS.get(stage);

/**
 * The parcours of one file: the kit's `Track`, one segment per stage. A stage before the file's is `done`, the
 * file's own is `current`, the others `todo`. **No fill is invented**: the current segment's bar is empty,
 * because no screen holds a measure of how far through its stage a file is (`Track`'s default of half would be
 * a claim nobody made); the stage is said by the bold label and `aria-current`.
 */
export function StageStepper({
  stage,
  comite = true,
  revue = null,
}: {
  readonly stage: string;
  /** D2 `track()`: « Revue 3/15 » and the Revue segment filled by it, from the server's progression; `null` where the door gives none. */
  readonly revue?: { readonly handled: number; readonly total: number } | null;
  /** WF-BNK-11's acceptance: the `comite` step is hidden while the amount is inside the delegation (`L-1b`). */
  readonly comite?: boolean | undefined;
}): React.JSX.Element {
  const shown: readonly string[] = comite ? STAGES : STAGES.filter((one) => one !== "comite");
  const index = shown.indexOf(stage);
  const segments: TrackSegment[] = shown.map((one, i) => ({
    key: one,
    testId: `bank-etape-${one}`,
    label: STEP_LABELS.get(one) ?? one,
    state: i < index ? "done" : i === index ? "current" : "todo",
    ...(i === index && one === "revue" && revue !== null && revue.total > 0
      ? {
          fill: (revue.handled / revue.total) * 100,
          count: `${String(revue.handled)}/${String(revue.total)}`,
        }
      : i === index
        ? { fill: 0 }
        : {}),
  }));
  return (
    <div data-testid="bank-stepper">
      <Track label={t("bank.dossier.etape")} segments={segments} />
    </div>
  );
}

export function Workspace(props: {
  readonly file: FichierBanque;
  readonly onPipeline: () => void;
  /** `L-1b`: the committee's question as the offer's read derives it (the policy in force before the decision). */
  readonly comite?: boolean | undefined;
  /** `L-5`, `M3`: why the file is at `perdu`, from the row (`lost`), for the header's chip. */
  readonly lost?: { readonly reason: string; readonly at: string; readonly detail?: string | null } | null;
  readonly busy: boolean;
  /** `ARC-3` (D2 `head()`): the vehicle's year, from the offer door's facts, read at every stage; `null` leaves it out of the sub line. */
  readonly vehicleYear?: number | null;
  /** `ARC-3`: the analyst's bank, from her session; `null` leaves it out of the sub line. */
  readonly bankName?: string | null;
  /** `ARC-3` (D2 `.medal`): the run's grade where the stage serves the analysis; `null` and the medal draws the file's letter alone. */
  readonly grade?: AnalysisGradeView | null;
  /** `ARC-3` (D2 `track()`): the review's progress as the file's own door counts it; `null` outside the review. */
  readonly revue?: { readonly handled: number; readonly total: number } | null;
  /** Lane E, M2: the report of the submitted file (paper, sheet, provenance, validity), drawn above the stage's panel. */
  readonly rapport?: ReactNode;
  /** `C1` (D2 `app.js:25`, `inRev`): the composer is on screen, so the title reads « Offre indicative · {name} »; the rest of the head stands. */
  readonly composing?: boolean | undefined;
  /** `A-1b-1`: the nouveau stage's landing, the briefing; drawn only at `nouveau`. */
  readonly briefing?: ReactNode;
  /** `A-1b-2a` (D3 R-5): the synthesis of the dossier behind its button, at every stage, above the stage's own module. */
  readonly synthesis?: ReactNode;
  /**
   * `S1-a` COMPORTEMENT DU COMPTE (`D2-behaviour.md` §3): the tab strip under the stage track, ONCE for every stage. Absent, nothing is drawn and the screen is exactly what it was. With
   * `active: "comportement"` the tab's content stands where the dossier's own content stood (the report, the synthesis and the stage's panel are not drawn under it); the head and the stage track stay.
   */
  readonly tabs?: {
    readonly strip: ReactNode;
    readonly active: "dossier" | "comportement";
    readonly behaviour: ReactNode;
  };
  readonly children?: ReactNode;
}): React.JSX.Element {
  const { file } = props;
  // D2 `head()`: « véhicule année · montant · n mois demandés · banque »; what the file does not hold is left out, never a dash or a figure.
  const subLine = headSubLineParts({
    vehicleLabel: file.vehicleLabel,
    vehicleYear: props.vehicleYear ?? null,
    amountFinanced: file.amountFinanced,
    requestedMonths: file.durationMonths,
    bankName: props.bankName ?? null,
  }).join(" · ");
  const grade = props.grade ?? null;
  const incomplete = (grade?.incomplete ?? null) !== null;
  // The letter is the file's (her acts re-derive it); the mean is the run's and only beside the SAME letter.
  const mean = grade !== null && grade.grade !== null && grade.grade === file.grade ? grade.average : null;
  return (
    <div data-testid="bank-revue">
      {/* `F-FLOOR-2`: the breadcrumb is one line of metadata, 13 px (the theme's meta size), never the 12 of `text-xs`. D2: « Pipeline › référence ». */}
      <div className={`mb-3 flex items-center gap-1.5 ${BANK_FS_META} text-gray-600`}>
        <button
          type="button"
          onClick={props.onPipeline}
          className="hover:text-indigo-600"
          data-testid="bank-fil-pipeline"
        >
          {t("bank.nav.pipeline")}
        </button>
        <ChevronRight className="h-3 w-3" aria-hidden="true" />
        <span className="font-medium text-gray-900">{file.reference}</span>
      </div>
      <div className="mb-[18px] mt-[14px] flex items-center gap-4" data-testid="bank-dossier-tete">
        <span
          aria-hidden="true"
          data-testid="bank-dossier-initiales"
          className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[14px] bg-[color:var(--hk-text)] text-lg font-semibold text-white"
        >
          {initialsOf(file.clientName)}
        </span>
        <div className="min-w-0">
          <h1
            className="m-0 text-[length:var(--hk-fs-h1)] font-semibold leading-[var(--hk-lh-tight)] tracking-[-.01em]"
            data-testid="bank-dossier-titre"
          >
            {props.composing === true
              ? fill("bank.composer.titreTete", { nom: file.clientName ?? DASH })
              : (file.clientName ?? DASH)}
          </h1>
          <p
            className="mt-[3px] text-[length:var(--hk-fs-read)] text-[color:var(--hk-text-2)]"
            data-testid="bank-dossier-sous-titre"
          >
            {subLine}
          </p>
        </div>
        <StatusChip stage={file.stage} words={chipWords(file.stage, props.lost ?? null)} />
        <span className="flex-1" />
        <GradeMedal
          letter={file.grade ?? null}
          mean={mean}
          provisional={grade?.provisional === true}
          incomplete={incomplete}
        />
      </div>
      <div className="mb-6">
        <StageStepper
          stage={file.stage}
          comite={props.comite ?? file.requiresComite}
          revue={file.stage === "revue" ? (props.revue ?? null) : null}
        />
      </div>
      {props.tabs?.strip ?? null}
      {props.tabs?.active === "comportement" ? (
        props.tabs.behaviour
      ) : (
        <>
          {props.rapport ?? null}
          {props.synthesis ?? null}
          <div className="mb-4">
            {file.stage === "nouveau"
              ? // `A-1b-1`: the landing is the briefing (the screen hands it in); the banner sentence and the bare button are gone.
                (props.briefing ?? null)
              : file.stage === "revue" ||
                  file.stage === "retour" ||
                  file.stage === "validation" ||
                  file.stage === "offre" ||
                  file.stage === "comite" ||
                  file.stage === "signature" ||
                  // `T-1b`: the conditions panel rides the same slot.
                  file.stage === "conditions" ||
                  // `T-2`: the terminal panel (WF-BNK-17) rides it too , found at the park's shooting.
                  file.stage === "decaisse" ||
                  file.stage === "perdu" ||
                  // `L-3b`: the refuse state's banner rides the same slot.
                  file.stage === "refuse"
                ? // `L-1b`: the validation panel and the offer-at-client panel ride the same slot as the review;
                  // `L-2`: the answered panel and the next stage's words, after the buyer's choice (F5).
                  props.children
                : null}
          </div>
        </>
      )}
    </div>
  );
}
